import React, { useState } from "react";
import { X, XCircle, User, AlertTriangle, FileText } from "lucide-react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import affiliateService from "../../requests/affiliates/AffiliateService";

const RejectApplicationModal = ({ application, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [showCustomReason, setShowCustomReason] = useState(false);

  const predefinedReasons = [
    "Insufficient reach/followers",
    "Inappropriate content or brand alignment",
    "Incomplete application information",
    "Duplicate application",
    "Does not meet minimum requirements",
    "Other",
  ];

  const handleReject = async () => {
    if (!rejectReason) {
      toast.error("Please select a reason for rejection");
      return;
    }

    if (rejectReason === "Other" && !customReason.trim()) {
      toast.error("Please provide a custom reason");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: application.id,
        status: "rejected",
        reject_reason: rejectReason === "Other" ? customReason : rejectReason,
      };

      const response = await affiliateService.rejectApplication(
        application.id,
        payload
      );
      if (response?.data?.status_code === "000") {
        toast.success(
          response?.data?.reason || "Application rejected successfully"
        );
        onSuccess();
      } else {
        toast.error(response?.data?.reason || "Failed to reject application");
      }
    } catch (error) {
      toast.error("Failed to reject application");
    } finally {
      setLoading(false);
    }
  };

  const affiliate = application.affiliate;
  const deal = application.deal;

  return (
    <AnimatePresence>
      <motion.div
        className="overflow-y-auto fixed inset-0 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Backdrop */}
        <motion.div
          className="fixed inset-0 z-40 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        ></motion.div>

        <div className="flex relative z-50 justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
          {/* Modal */}
          <motion.div
            className="inline-block overflow-hidden relative z-50 text-left align-bottom bg-white rounded-2xl shadow-xl transition-all transform sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-red-600">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <XCircle className="mr-3 w-6 h-6 text-white" />
                  <h3 className="text-lg font-semibold text-white">
                    Reject Application
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-white transition-colors hover:text-red-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 bg-white">
              {/* Application Summary */}
              <div className="mb-4">
                <div className="flex items-center mb-2 space-x-3">
                  <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      {affiliate.first_name} {affiliate.last_name}
                    </h4>
                    <p className="text-xs text-gray-600">{affiliate.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex justify-center items-center w-8 h-8 bg-purple-100 rounded-full">
                    <FileText className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      {deal.title}
                    </h4>
                    <p className="text-xs text-gray-600">{deal.brand_name}</p>
                  </div>
                </div>
              </div>

              {/* Rejection Reason */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Reason for rejection
                </label>
                <div className="space-y-2">
                  {predefinedReasons.map((reason) => (
                    <label key={reason} className="flex items-center">
                      <input
                        type="radio"
                        name="rejectReason"
                        value={reason}
                        checked={rejectReason === reason}
                        onChange={(e) => {
                          setRejectReason(e.target.value);
                          setShowCustomReason(e.target.value === "Other");
                        }}
                        className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {reason}
                      </span>
                    </label>
                  ))}
                </div>

                {showCustomReason && (
                  <div className="mt-3">
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Please explain why this application is being rejected..."
                      rows={3}
                      className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This reason will be shared with the affiliate.
                    </p>
                  </div>
                )}
              </div>

              {/* Warning */}
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 mr-2" />
                  <p className="text-sm text-red-800">
                    This action cannot be easily reversed. The affiliate will be
                    notified of the rejection.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-3 space-x-3 bg-gray-50">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={
                  loading ||
                  !rejectReason ||
                  (rejectReason === "Other" && !customReason.trim())
                }
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg border border-transparent transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="mr-2 w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle className="mr-2 w-4 h-4" />
                    Reject
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RejectApplicationModal;
